import type {
  RAGConfig,
  RAGEnv,
  RAGPipelineInfo,
  RAGPipelineStatus,
  IngestionRequest,
  QueryRequest,
  QueryResponse,
  QueryStreamChunk,
  BatchProcessingResult,
} from '../types';
import { RAGError } from '../types';
import { RAGPipeline } from './rag-pipeline';

/**
 * RAG Manager that handles multiple RAG pipelines
 */
export class RAGManager {
  private env: RAGEnv;
  private pipelines: Map<string, RAGPipeline> = new Map();
  private initialized = false;

  constructor(env: RAGEnv) {
    this.env = env;
  }

  /**
   * Initialize the RAG manager
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Load existing pipelines from storage
      await this.loadExistingPipelines();
      this.initialized = true;
    } catch (error) {
      throw new RAGError(
        `RAG Manager initialization failed: ${error}`,
        'MANAGER_INIT_ERROR',
        500
      );
    }
  }

  /**
   * Create a new RAG pipeline
   */
  async createPipeline(config: RAGConfig): Promise<RAGPipeline> {
    if (!this.initialized) {
      throw new RAGError('RAG Manager not initialized', 'MANAGER_NOT_INITIALIZED', 503);
    }

    if (this.pipelines.has(config.name)) {
      throw new RAGError(
        `Pipeline '${config.name}' already exists`,
        'PIPELINE_EXISTS',
        409
      );
    }

    try {
      const pipeline = new RAGPipeline(this.env, config);
      await pipeline.initialize();

      this.pipelines.set(config.name, pipeline);

      // Store pipeline configuration
      await this.storePipelineList();

      return pipeline;
    } catch (error) {
      throw new RAGError(
        `Failed to create pipeline '${config.name}': ${error}`,
        'PIPELINE_CREATE_ERROR',
        500,
        config.name
      );
    }
  }

  /**
   * Get a pipeline by name
   */
  getPipeline(name: string): RAGPipeline | null {
    if (!this.initialized) {
      throw new RAGError('RAG Manager not initialized', 'MANAGER_NOT_INITIALIZED', 503);
    }

    return this.pipelines.get(name) || null;
  }

  /**
   * Get pipeline or throw error if not found
   */
  private requirePipeline(name: string): RAGPipeline {
    const pipeline = this.getPipeline(name);
    if (!pipeline) {
      throw new RAGError(
        `Pipeline '${name}' not found`,
        'PIPELINE_NOT_FOUND',
        404,
        name
      );
    }
    return pipeline;
  }

  /**
   * List all pipelines
   */
  async listPipelines(): Promise<RAGPipelineInfo[]> {
    if (!this.initialized) {
      throw new RAGError('RAG Manager not initialized', 'MANAGER_NOT_INITIALIZED', 503);
    }

    const infos: RAGPipelineInfo[] = [];

    for (const pipeline of this.pipelines.values()) {
      try {
        const info = await pipeline.getInfo();
        infos.push(info);
      } catch (error) {
        console.warn(`Failed to get info for pipeline ${pipeline.getConfig().name}:`, error);
      }
    }

    return infos;
  }

  /**
   * Delete a pipeline
   */
  async deletePipeline(name: string): Promise<void> {
    if (!this.initialized) {
      throw new RAGError('RAG Manager not initialized', 'MANAGER_NOT_INITIALIZED', 503);
    }

    const pipeline = this.requirePipeline(name);

    try {
      await pipeline.delete();
      this.pipelines.delete(name);

      // Update pipeline list
      await this.storePipelineList();
    } catch (error) {
      throw new RAGError(
        `Failed to delete pipeline '${name}': ${error}`,
        'PIPELINE_DELETE_ERROR',
        500,
        name
      );
    }
  }

  /**
   * Update a pipeline configuration
   */
  async updatePipeline(name: string, config: Partial<RAGConfig>): Promise<void> {
    if (!this.initialized) {
      throw new RAGError('RAG Manager not initialized', 'MANAGER_NOT_INITIALIZED', 503);
    }

    const pipeline = this.requirePipeline(name);

    try {
      await pipeline.updateConfig(config);
    } catch (error) {
      throw new RAGError(
        `Failed to update pipeline '${name}': ${error}`,
        'PIPELINE_UPDATE_ERROR',
        500,
        name
      );
    }
  }

  /**
   * Ingest documents into a pipeline
   */
  async ingest(
    pipelineName: string,
    request: IngestionRequest | IngestionRequest[]
  ): Promise<BatchProcessingResult> {
    const pipeline = this.requirePipeline(pipelineName);
    return pipeline.ingest(request);
  }

  /**
   * Query a pipeline
   */
  async query(pipelineName: string, request: QueryRequest): Promise<QueryResponse> {
    const pipeline = this.requirePipeline(pipelineName);
    return pipeline.query(request);
  }

  /**
   * Query a pipeline with streaming response
   */
  async queryStream(
    pipelineName: string,
    request: QueryRequest,
    onChunk?: (chunk: QueryStreamChunk) => void
  ): Promise<ReadableStream<QueryStreamChunk>> {
    const pipeline = this.requirePipeline(pipelineName);
    return pipeline.queryStream(request, onChunk);
  }

  /**
   * Get pipeline information
   */
  async getPipelineInfo(name: string): Promise<RAGPipelineInfo> {
    const pipeline = this.requirePipeline(name);
    return pipeline.getInfo();
  }

  /**
   * Check pipeline health
   */
  async checkHealth(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy';
    pipelines: Array<{
      name: string;
      status: RAGPipelineStatus;
      error?: string;
    }>;
    totalPipelines: number;
    healthyPipelines: number;
  }> {
    if (!this.initialized) {
      return {
        status: 'unhealthy',
        pipelines: [],
        totalPipelines: 0,
        healthyPipelines: 0,
      };
    }

    const pipelineStatuses = Array.from(this.pipelines.entries()).map(([name, pipeline]) => {
      const status = pipeline.getStatus();
      return {
        name,
        status,
        ...(status === 'error' ? { error: 'Pipeline error' } : {}),
      };
    });

    const totalPipelines = pipelineStatuses.length;
    const healthyPipelines = pipelineStatuses.filter(p => p.status === 'ready').length;
    const errorPipelines = pipelineStatuses.filter(p => p.status === 'error').length;

    let overallStatus: 'healthy' | 'degraded' | 'unhealthy';
    if (totalPipelines === 0) {
      overallStatus = 'healthy'; // No pipelines is considered healthy
    } else if (errorPipelines === 0) {
      overallStatus = 'healthy';
    } else if (healthyPipelines > 0) {
      overallStatus = 'degraded';
    } else {
      overallStatus = 'unhealthy';
    }

    return {
      status: overallStatus,
      pipelines: pipelineStatuses,
      totalPipelines,
      healthyPipelines,
    };
  }

  /**
   * Get manager statistics
   */
  async getStats(): Promise<{
    totalPipelines: number;
    pipelinesByStatus: Record<RAGPipelineStatus, number>;
    totalDocuments: number;
    totalChunks: number;
    totalQueries: number;
    avgResponseTime: number;
  }> {
    if (!this.initialized) {
      throw new RAGError('RAG Manager not initialized', 'MANAGER_NOT_INITIALIZED', 503);
    }

    const pipelineInfos = await this.listPipelines();

    const pipelinesByStatus: Record<RAGPipelineStatus, number> = {
      initializing: 0,
      ready: 0,
      processing: 0,
      error: 0,
      disabled: 0,
    };

    let totalDocuments = 0;
    let totalChunks = 0;
    let totalQueries = 0;
    let totalResponseTime = 0;

    for (const info of pipelineInfos) {
      pipelinesByStatus[info.status]++;
      totalDocuments += info.stats.documentsCount;
      totalChunks += info.stats.chunksCount;
      totalQueries += info.stats.totalQueries;
      totalResponseTime += info.stats.avgResponseTime * info.stats.totalQueries;
    }

    const avgResponseTime = totalQueries > 0 ? totalResponseTime / totalQueries : 0;

    return {
      totalPipelines: pipelineInfos.length,
      pipelinesByStatus,
      totalDocuments,
      totalChunks,
      totalQueries,
      avgResponseTime,
    };
  }

  /**
   * Create a default RAG pipeline with optimized settings
   */
  async createDefaultPipeline(name: string, description?: string): Promise<RAGPipeline> {
    const config: RAGConfig = {
      name,
      description: description || `Default RAG pipeline: ${name}`,
      embedding: {
        model: this.env.RAG_DEFAULT_EMBEDDING_MODEL || 'text-embedding-ada-002',
        provider: 'openai',
        dimensions: 1536,
        batchSize: parseInt(this.env.RAG_BATCH_SIZE || '100'),
        maxRetries: 3,
        timeout: 30000,
      },
      chunking: {
        strategy: 'recursive',
        size: 512,
        overlap: 128,
        minChunkSize: 50,
        maxChunkSize: 2048,
        preserveStructure: false,
        separators: ['\n\n', '\n', '. ', ' ', ''],
      },
      retrieval: {
        topK: 5,
        minSimilarity: 0.7,
        hybridWeight: 0.7,
        maxContextTokens: parseInt(this.env.RAG_MAX_CONTEXT_TOKENS || '4000'),
      },
      generation: {
        model: this.env.RAG_DEFAULT_GENERATION_MODEL || 'gpt-3.5-turbo',
        provider: 'openai',
        temperature: 0.1,
        maxTokens: 1000,
        includeSource: true,
        streaming: false,
      },
      analytics: {
        enabled: true,
        trackQueries: true,
        trackPerformance: true,
        sampleRate: 1.0,
      },
    };

    return this.createPipeline(config);
  }

  /**
   * Bulk operations for multiple pipelines
   */
  async bulkQuery(
    queries: Array<{
      pipelineName: string;
      request: QueryRequest;
    }>
  ): Promise<Array<{
    pipelineName: string;
    response?: QueryResponse;
    error?: string;
  }>> {
    const results = await Promise.allSettled(
      queries.map(async ({ pipelineName, request }) => ({
        pipelineName,
        response: await this.query(pipelineName, request),
      }))
    );

    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        return {
          pipelineName: queries[index]?.pipelineName || 'unknown',
          error: result.reason instanceof Error ? result.reason.message : 'Unknown error',
        };
      }
    });
  }

  /**
   * Load existing pipelines from storage
   */
  private async loadExistingPipelines(): Promise<void> {
    try {
      const pipelineListJson = await this.env.RAG_KV.get('rag:pipelines');
      if (!pipelineListJson) return;

      const pipelineNames: string[] = JSON.parse(pipelineListJson);

      for (const name of pipelineNames) {
        try {
          const configJson = await this.env.RAG_KV.get(`pipeline:${name}:config`);
          if (configJson) {
            const { config, status } = JSON.parse(configJson);
            const pipeline = new RAGPipeline(this.env, config);

            if (status !== 'error') {
              await pipeline.initialize();
            }

            this.pipelines.set(name, pipeline);
          }
        } catch (loadError) {
          console.warn(`Failed to load pipeline ${name}:`, loadError);
        }
      }
    } catch (error) {
      console.warn('Failed to load existing pipelines:', error);
    }
  }

  /**
   * Store the list of pipeline names
   */
  private async storePipelineList(): Promise<void> {
    const pipelineNames = Array.from(this.pipelines.keys());
    await this.env.RAG_KV.put('rag:pipelines', JSON.stringify(pipelineNames));
  }

  /**
   * Clean up resources
   */
  async cleanup(): Promise<void> {
    // In a real implementation, you might want to gracefully shut down pipelines
    this.pipelines.clear();
    this.initialized = false;
  }
}
